/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Briefcase, Plus, Eye, Edit, DollarSign,
  TrendingUp, Building2, RefreshCw, ChevronLeft, ChevronRight, CheckCircle
} from 'lucide-react';
import { usePartners } from '@/hooks/api/usePartners';
import { PartnerListItem, PartnerStatus, PartnerType } from '@/types/superadmin.types';
import { PartnerDetailModal } from '@/components/superadmin/PartnerDetailModal';
import { SortableHeader, useSort } from '@/components/ui/SortableHeader';
import { SearchInput } from '@/components/ui';

// Status Badge
const StatusBadge: React.FC<{ status: PartnerStatus; t: (key: string) => string }> = ({ status, t }) => {
  const styles: Record<PartnerStatus, string> = {
    ACTIVE: 'bg-emerald-500/15 text-emerald-500',
    PENDING: 'bg-amber-500/15 text-amber-500',
    SUSPENDED: 'bg-orange-500/15 text-orange-500',
    TERMINATED: 'bg-red-500/15 text-red-500',
  };
  const labelKeys: Record<PartnerStatus, string> = {
    ACTIVE: 'partners.status.active',
    PENDING: 'partners.status.pending',
    SUSPENDED: 'partners.status.suspended',
    TERMINATED: 'partners.status.terminated',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
      {t(labelKeys[status])}
    </span>
  );
};

// Type Badge
const TypeBadge: React.FC<{ type: PartnerType; t: (key: string) => string }> = ({ type, t }) => {
  const styles = {
    REFERRAL: 'bg-blue-500/15 text-blue-500',
    RESELLER: 'bg-purple-500/15 text-purple-500',
    AFFILIATE: 'bg-orange-500/15 text-orange-500',
  };
  const labelKeys: Record<PartnerType, string> = {
    REFERRAL: 'partners.type.referral',
    RESELLER: 'partners.type.reseller',
    AFFILIATE: 'partners.type.affiliate',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${styles[type]}`}>
      {t(labelKeys[type])}
    </span>
  );
};

// Skeleton Row Component
const SkeletonRow: React.FC = () => (
  <div className="partners-table-row grid grid-cols-[2fr_100px_100px_100px_100px_100px_80px] gap-4 px-4 py-3 items-center border-b border-border-subtle animate-pulse">
    <div>
      <div className="h-4 w-36 bg-muted/20 rounded mb-2"></div>
      <div className="h-3 w-28 bg-muted/20 rounded"></div>
    </div>
    <div className="h-5 w-16 bg-muted/20 rounded col-type"></div>
    <div className="h-5 w-14 bg-muted/20 rounded-full"></div>
    <div className="h-4 w-10 bg-muted/20 rounded col-commission"></div>
    <div className="h-4 w-8 bg-muted/20 rounded col-referrals"></div>
    <div className="h-4 w-16 bg-muted/20 rounded col-earnings"></div>
    <div className="flex gap-1">
      <div className="w-7 h-7 bg-muted/20 rounded"></div>
      <div className="w-7 h-7 bg-muted/20 rounded"></div>
    </div>
  </div>
);

// Stat Card Skeleton
const StatCardSkeleton: React.FC = () => (
  <div className="bg-surface border border-border-subtle rounded-xl p-4 animate-pulse">
    <div className="w-10 h-10 rounded-lg bg-muted/20 mb-2"></div>
    <div className="h-6 w-16 bg-muted/20 rounded mb-1"></div>
    <div className="h-3 w-24 bg-muted/20 rounded"></div>
  </div>
);

// Stat Card
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string;
  label: string;
  color: 'green' | 'blue' | 'purple' | 'orange';
}> = ({ icon, value, label, color }) => {
  const colors = {
    green: 'bg-emerald-500/15 text-emerald-500',
    blue: 'bg-blue-500/15 text-blue-500',
    purple: 'bg-purple-500/15 text-purple-500',
    orange: 'bg-orange-500/15 text-orange-500',
  };

  return (
    <div className="bg-surface border border-border-subtle rounded-xl p-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${colors[color]}`}>
        {icon}
      </div>
      <div className="font-mono text-3xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted">{label}</div>
    </div>
  );
};

// Main Partners Page
export default function PartnersPage() {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PartnerStatus | 'ALL'>('ALL');
  const [currentPage, setCurrentPage] = useState(0);

  // Sort state
  const { sortConfig, handleSort, getSortParam } = useSort('companyName', 'asc');

  // Detail modal state
  const [selectedPartner, setSelectedPartner] = useState<PartnerListItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Use the partners hook with filters
  const { data, isLoading, isFetching, refetch } = usePartners({
    page: currentPage,
    size: 20,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    search: searchQuery || undefined,
    sort: getSortParam(),
  });

  const partners = data?.content || [];
  const totalElements = data?.page?.totalElements || 0;
  const totalPages = data?.page?.totalPages || 1;

  // Calculate stats from current data
  const stats = useMemo(() => {
    const activePartners = partners.filter(p => p.status === 'ACTIVE').length;
    const totalReferrals = partners.reduce((sum, p) => sum + p.activeReferrals, 0);
    const totalEarnings = partners.reduce((sum, p) => sum + p.totalEarnings, 0);
    const pendingPayouts = partners.reduce((sum, p) => sum + p.pendingPayout, 0);
    return {
      activePartners,
      totalReferrals,
      totalEarnings,
      pendingPayouts,
    };
  }, [partners]);

  // Handle view partner detail
  const handleViewDetail = (partner: PartnerListItem) => {
    setSelectedPartner(partner);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setSelectedPartner(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="partners-header flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Briefcase className="w-6 h-6" /> {t('partners.title')}
          </h1>
          <p className="text-sm text-muted">{t('partners.subtitle')}</p>
        </div>
        <div className="partners-actions flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg border border-border hover:bg-hover disabled:opacity-50"
            title={t('common.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => navigate('/superadmin/partners/new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" /> <span className="btn-text">{t('partners.addPartner')}</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={<Briefcase className="w-5 h-5" />}
              value={stats.activePartners.toString()}
              label={t('partners.stats.activePartners')}
              color="purple"
            />
            <StatCard
              icon={<Building2 className="w-5 h-5" />}
              value={stats.totalReferrals.toString()}
              label={t('partners.stats.activeReferrals')}
              color="blue"
            />
            <StatCard
              icon={<DollarSign className="w-5 h-5" />}
              value={`$${stats.totalEarnings.toLocaleString()}`}
              label={t('partners.stats.totalPaidOut')}
              color="green"
            />
            <StatCard
              icon={<TrendingUp className="w-5 h-5" />}
              value={`$${stats.pendingPayouts.toLocaleString()}`}
              label={t('partners.stats.pendingPayouts')}
              color="orange"
            />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('partners.searchPlaceholder')}
          className="flex-1 max-w-xs"
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as PartnerStatus | 'ALL');
            setCurrentPage(0);
          }}
          className="px-3 py-2 bg-background border border-border-subtle rounded-lg text-sm text-foreground focus:outline-none"
        >
          <option value="ALL">{t('partners.filter.allStatus')}</option>
          <option value="ACTIVE">{t('partners.status.active')}</option>
          <option value="PENDING">{t('partners.status.pending')}</option>
          <option value="SUSPENDED">{t('partners.status.suspended')}</option>
          <option value="TERMINATED">{t('partners.status.terminated')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
        <div className="partners-table-header grid grid-cols-[2fr_100px_100px_100px_100px_100px_80px] gap-4 px-4 py-3 bg-background border-b border-border-subtle">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-partner">
            <SortableHeader
              columnKey="companyName"
              label={t('partners.table.partner')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-type">
            <SortableHeader
              columnKey="partnerType"
              label={t('partners.table.type')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-status">
            <SortableHeader
              columnKey="status"
              label={t('partners.table.status')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-commission">
            <SortableHeader
              columnKey="commissionRate"
              label={t('partners.table.commission')}
              sortConfig={sortConfig}
              onSort={handleSort}
              align="right"
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-referrals">
            <SortableHeader
              columnKey="activeReferrals"
              label={t('partners.table.referrals')}
              sortConfig={sortConfig}
              onSort={handleSort}
              align="right"
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-earnings">
            <SortableHeader
              columnKey="totalEarnings"
              label={t('partners.table.earnings')}
              sortConfig={sortConfig}
              onSort={handleSort}
              align="right"
            />
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted col-actions">{t('partners.table.actions')}</div>
        </div>

        {isLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : partners.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted">
            <Briefcase className="w-12 h-12 mb-3 opacity-50" />
            <p>{t('partners.emptyState')}</p>
          </div>
        ) : (
          partners.map((partner) => (
            <div
              key={partner.id}
              onClick={() => navigate(`/superadmin/partners/${partner.id}`)}
              className="partners-table-row grid grid-cols-[2fr_100px_100px_100px_100px_100px_80px] gap-4 px-4 py-3 items-center border-b border-border-subtle last:border-b-0 hover:bg-hover cursor-pointer"
            >
              <div className="col-partner">
                <div className="font-medium text-foreground text-base">{partner.name}</div>
                <div className="text-sm text-muted">{partner.email}</div>
              </div>
              <div className="col-type"><TypeBadge type={partner.type} t={t} /></div>
              <div className="col-status"><StatusBadge status={partner.status} t={t} /></div>
              <div className="font-mono text-base text-foreground col-commission flex items-center gap-1">
                {partner.commissionRate}%
                {partner.autoApproveCommissions && (
                  <span title={t('partners.autoApproveEnabled')} className="text-emerald-500">
                    <CheckCircle className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>
              <div className="text-base text-foreground col-referrals">{partner.activeReferrals}</div>
              <div className="font-mono text-base text-emerald-500 col-earnings">${partner.totalEarnings.toLocaleString()}</div>
              <div className="col-actions flex gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => handleViewDetail(partner)}
                  className="p-1.5 rounded hover:bg-hover transition-colors text-muted hover:text-foreground"
                  title={t('partners.viewDetails')}
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate(`/superadmin/partners/${partner.id}/edit`)}
                  className="p-1.5 rounded hover:bg-hover transition-colors text-muted hover:text-foreground"
                  title={t('partners.editPartner')}
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted">
          {totalElements > 0 ? t('partners.pagination.showing', {
            from: currentPage * 20 + 1,
            to: Math.min((currentPage + 1) * 20, totalElements),
            total: totalElements
          }) : t('partners.pagination.noResults')}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted">
            {t('partners.pagination.page', { current: currentPage + 1, total: Math.max(1, totalPages) })}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Partner Detail Modal */}
      <PartnerDetailModal
        partner={selectedPartner}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetail}
      />

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .partners-header {
            flex-direction: column;
            align-items: stretch;
          }
          .partners-actions {
            width: 100%;
            justify-content: flex-end;
          }
          .partners-actions .btn-text {
            display: none;
          }
          .partners-table-header,
          .partners-table-row {
            display: flex !important;
            align-items: center;
            gap: 8px !important;
          }
          .col-type,
          .col-commission,
          .col-referrals,
          .col-earnings {
            display: none !important;
          }
          .col-partner {
            flex: 1;
            min-width: 0;
          }
          .col-status {
            flex-shrink: 0;
            text-align: center;
          }
          .col-actions {
            flex-shrink: 0;
            display: flex;
            justify-content: flex-end;
          }
        }
      `}</style>
    </div>
  );
}
